import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, Loader2, MessageSquareText, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1602943543714-cf535b048440?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user && user.id) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full lg:grid lg:grid-cols-2 bg-ivory">
      {/* Brand panel */}
      <div className="relative hidden lg:block">
        <img src={BG} alt="Tea plantation" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-forest-dark/80" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-ivory">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-gold text-forest-dark">
              <Leaf className="h-6 w-6" />
            </div>
            <div>
              <div className="font-heading text-lg font-bold">Prime Gold Tea</div>
              <div className="text-xs uppercase tracking-widest text-gold-bg/80">Sales Copilot</div>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="font-heading text-4xl font-bold leading-tight">
              Communicate with confidence, every message.
            </h1>
            <p className="mt-4 text-ivory/80 leading-relaxed">
              A guided assistant that understands each customer's situation, recommends the right
              objective, and drafts a WhatsApp-ready message in English, Hindi or Hinglish.
            </p>
            <div className="mt-8 flex items-center gap-2 text-sm text-ivory/70">
              <MessageSquareText className="h-4 w-4 text-gold-bg" />
              Manual WhatsApp only — no automation, no fake read status.
            </div>
          </div>
          <div className="text-xs text-ivory/50">© Prime Gold Tea · Assam & Dooars CTC</div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 lg:hidden flex items-center gap-2.5">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-forest text-gold">
              <Leaf className="h-5 w-5" />
            </div>
            <div>
              <div className="font-heading font-bold text-forest">Prime Gold Tea</div>
              <div className="text-[10px] uppercase tracking-widest text-gold font-semibold">Sales Copilot</div>
            </div>
          </div>

          <h2 className="font-heading text-2xl font-bold text-stone-900">Sign in</h2>
          <p className="mt-1 text-sm text-stone-500">Access your sales workspace.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@primegoldtea.com"
                data-testid="login-email-input"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-10"
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-stone-400 hover:text-stone-600 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-200" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-forest hover:bg-forest-600 text-ivory h-11"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
